import _ from "lodash"
import booleanPointInPolygon from "@turf/boolean-point-in-polygon"
import { Coord } from "@turf/helpers"
import { Feature } from "geojson"
import { Section, Unit, Venue, ImdfFeature } from "providers/venue/types"
import { GeolocationFeature } from "interfaces"

export const bindLevel = (feature: ImdfFeature, groupOrdinals): ImdfFeature => {
  if (!feature) return
  const geolocationFeatureOrdinal = _.get(feature, "properties.ordinal")
  const properties = _.get(feature, "properties", {})
  const level = _.get(groupOrdinals, geolocationFeatureOrdinal)
  const level_name = _.get(level, "properties.name.en")
  return {
    ...feature,
    properties: {
      ...properties,
      level_name,
      level,
    },
  }
}

export const bindVenue = (
  feature: ImdfFeature,
  venues: Venue[]
): ImdfFeature => {
  if (!feature) return
  const properties = _.get(feature, "properties", {})
  const geolocationCoordinates = (feature?.geometry?.coordinates || []) as Coord
  const venue = venues.find((venue) => {
    const { geometry: venueGeometry } = venue
    return booleanPointInPolygon(geolocationCoordinates, venueGeometry)
  })

  return {
    ...feature,
    properties: {
      ...properties,
      venue,
    },
  }
}

const bindUnit = (feature: ImdfFeature, units: Unit[]): Feature => {
  if (!feature) return
  const properties = _.get(feature, "properties", {})

  const geolocationCoordinates = (feature?.geometry?.coordinates || []) as Coord
  const unit = units.find((unit) => {
    try {
      const { geometry: venueGeometry } = unit
      return (
        booleanPointInPolygon(geolocationCoordinates, venueGeometry) || null
      )
    } catch (error) {
      return null
    }
  })
  return {
    ...feature,
    properties: {
      ...properties,
      unit,
    },
  }
}

// Among all sections whose polygon contains the point, prefer the most-specific
// one (deepest `parents` chain — i.e. a subsection over its parent).
const bindSection = (
  feature: ImdfFeature,
  sections: Section[]
): ImdfFeature => {
  if (!feature) return
  const properties = _.get(feature, "properties", {})

  const geolocationCoordinates = (feature?.geometry?.coordinates || []) as Coord
  const containingSections = sections.filter((section) => {
    try {
      return booleanPointInPolygon(geolocationCoordinates, section.geometry)
    } catch (error) {
      return false
    }
  })
  const section = _.maxBy(
    containingSections,
    (s) => s.properties?.parents?.length ?? 0
  )

  return {
    ...feature,
    properties: {
      ...properties,
      section,
    },
  }
}
export const prepareGeolocationFeature = (
  geolocationFeature: GeolocationFeature,
  featureObj = { venues: [], levels: [], units: [], sections: [] }
) => {
  const { venues = [], levels = [], units = [], sections = [] } = featureObj
  const obj = _.cloneDeep(geolocationFeature)
  const geolocationWithVenue = bindVenue(obj, venues)

  // Level Binding
  const locationVenueId = _.get(geolocationWithVenue, "properties.venue.id")
  const levelsInVenue = levels.filter(
    (level) => level?.properties?.venue?.id === locationVenueId
  )
  const groupedLevelByOrdinal = _.keyBy(levelsInVenue, "properties.ordinal")
  const geolocationWithLevel = bindLevel(
    geolocationWithVenue,
    groupedLevelByOrdinal
  )
  const locationLevel = geolocationWithLevel?.properties?.level

  if (!locationLevel) return geolocationWithLevel
  const unitInLevel = units.filter(
    (unit) =>
      unit.properties.level.id === geolocationWithLevel?.properties.level.id
  )
  const geolocationWithUnit = bindUnit(geolocationWithLevel, unitInLevel)

  const sectionsInLevel = sections.filter(
    (section) =>
      section.properties.level_id === geolocationWithLevel?.properties.level.id
  )
  const geolocationWithSection = bindSection(
    geolocationWithUnit as ImdfFeature,
    sectionsInLevel
  )
  return geolocationWithSection
}
